import type { UpcomingMeetingState } from './meeting-row-state';

/**
 * What an upcoming meeting's "Join this meeting" chip does, by the meeting's time and her live state.
 *
 * Before the meeting the chip sends her (the Meetings list's own Join request); from a minute before the
 * start it opens the meeting link, and sends her too if she is not coming. A status line in the Meetings
 * list's words sits under it throughout. A chip that would do nothing is not drawn.
 *
 * Import-free for the same reason as `meeting-row-state.ts`: a jest test must not reach react-query.
 */

/** common-lib `meetingJoinOptionId`, spelled out so this module stays import-free. */
const MEETING_JOIN_OPTION_PREFIX = 'meeting-join:';

/** The harness's `INLINE_JOIN_WINDOW_MS`: inside it a Join request joins her at once instead of arming. */
export const MEETING_JOIN_LIVE_LEAD_MS = 60 * 1000;

export interface MeetingJoinChipView {
  /** `send` asks her in and opens nothing; `open` opens the meeting link. */
  chip: 'send' | 'open' | null;
  requestsJoin: boolean;
  /** The state the status line names, or null for no line. */
  line: UpcomingMeetingState | null;
}

type MeetingTimes = { startsAt: string; endsAt?: string };

const NOTHING: MeetingJoinChipView = { chip: null, requestsJoin: false, line: null };

export function meetingJoinChipEventId(optionId: string): string | undefined {
  if (!optionId.startsWith(MEETING_JOIN_OPTION_PREFIX)) return undefined;
  return optionId.slice(MEETING_JOIN_OPTION_PREFIX.length) || undefined;
}

function hasEnded(meeting: MeetingTimes, now: number): boolean {
  const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
  return Number.isFinite(endsAt) && endsAt <= now;
}

function isLive(meeting: MeetingTimes, now: number): boolean {
  return now >= Date.parse(meeting.startsAt) - MEETING_JOIN_LIVE_LEAD_MS;
}

export function meetingJoinChipView(meeting: MeetingTimes & { state: UpcomingMeetingState }, now: number): MeetingJoinChipView {
  if (hasEnded(meeting, now) || meeting.state === 'failed') return NOTHING;
  // `paused` is the site's server-side switch: a request there is recorded but arms nothing.
  const requestsJoin = meeting.state === 'not_scheduled' || meeting.state === 'skipped';
  if (isLive(meeting, now)) return { chip: 'open', requestsJoin, line: meeting.state };
  return { chip: requestsJoin ? 'send' : null, requestsJoin, line: meeting.state };
}

/** When the view next changes on its own, so the chip can re-render then; undefined when it never will. */
export function nextMeetingJoinChange(meeting: MeetingTimes, now: number): number | undefined {
  if (hasEnded(meeting, now)) return undefined;
  if (!isLive(meeting, now)) return Date.parse(meeting.startsAt) - MEETING_JOIN_LIVE_LEAD_MS;
  const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
  return Number.isFinite(endsAt) ? endsAt : undefined;
}
